import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";

interface Batch {
  id: string;
  name: string;
  class: string;
}
interface Test {
  id: string;
  testName: string;
  class: string;
  batchId: string;
}
interface Student {
  id: string;
  name: string;
  class: string;
  batchId: string;
}
interface MarkInput {
  studentId: string;
  name: string;
  class: string;
  batchId: string;
  testId: string;
  testName: string;
  marks: string;
}

const classOptions = ["IX", "X", "XI", "XII"];

const Marks: React.FC = () => {
  const [selectedClass, setSelectedClass] = useState("");
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [tests, setTests] = useState<Test[]>([]);
  const [selectedTestId, setSelectedTestId] = useState("");
  const [students, setStudents] = useState<Student[]>([]);
  const [marks, setMarks] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // Fetch batches when class changes
  useEffect(() => {
    if (selectedClass) {
      fetchBatches(selectedClass);
      setSelectedBatchId("");
      setSelectedTestId("");
      setTests([]);
      setStudents([]);
      setMarks({});
    } else {
      setBatches([]);
      setSelectedBatchId("");
      setSelectedTestId("");
      setTests([]);
      setStudents([]);
      setMarks({});
    }
  }, [selectedClass]);

  // Fetch tests when batch changes
  useEffect(() => {
    if (selectedClass && selectedBatchId) {
      fetchTests(selectedClass, selectedBatchId);
      setSelectedTestId("");
      setStudents([]);
      setMarks({});
    } else {
      setTests([]);
      setSelectedTestId("");
      setStudents([]);
      setMarks({});
    }
  }, [selectedBatchId, selectedClass]);

  // Fetch students when test is selected
  useEffect(() => {
    if (selectedBatchId && selectedTestId) {
      fetchStudents(selectedBatchId);
      setMarks({});
    } else {
      setStudents([]);
      setMarks({});
    }
  }, [selectedBatchId, selectedTestId]);

  const fetchBatches = async (cls: string) => {
    const q = query(collection(db, "batches"), where("class", "==", cls));
    const snapshot = await getDocs(q);
    setBatches(snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) })));
  };
  const fetchTests = async (cls: string, batchId: string) => {
    const q = query(collection(db, "tests"), where("class", "==", cls), where("batchId", "==", batchId));
    const snapshot = await getDocs(q);
    setTests(snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) })));
  };
  const fetchStudents = async (batchId: string) => {
    const q = query(collection(db, "users"), where("batchId", "==", batchId));
    const snapshot = await getDocs(q);
    const studentsList = snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) }));
    setStudents(studentsList);

    // Fetch previous marks for these students for the selected test
    if (selectedTestId && studentsList.length > 0) {
      const marksQ = query(
        collection(db, "marks"),
        where("testId", "==", selectedTestId),
        where("studentId", "in", studentsList.map(s => s.id))
      );
      const marksSnap = await getDocs(marksQ);
      const marksMap: Record<string, string> = {};
      marksSnap.docs.forEach(doc => {
        const data = doc.data();
        if (data.studentId && data.marks !== undefined) {
          marksMap[data.studentId] = data.marks;
        }
      });
      setMarks(marksMap);
    } else {
      setMarks({});
    }
  };


  // Handle marks input
  const handleMarkChange = (studentId: string, value: string) => {
    setMarks(prev => ({ ...prev, [studentId]: value }));
  };

  // Save all marks
  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const test = tests.find(t => t.id === selectedTestId);
      if (!test) throw new Error("Test not found");
      for (const student of students) {
        const mark = marks[student.id];
        if (mark !== undefined && mark !== "") {
          await addDoc(collection(db, "marks"), {
            studentId: student.id,
            name: student.name,
            class: student.class,
            batchId: student.batchId,
            testId: test.id,
            testName: test.testName,
            marks: mark,
            createdAt: Timestamp.now(),
          });
        }
      }
      setSuccess("Marks saved successfully!");
    } catch (err) {
      setError("Failed to save marks.");
    }
    setSaving(false);
  };

  return (
    <div className="w-full max-w-5xl mx-auto mt-8">
      <h2 className="text-2xl font-bold mb-4">Marks Management</h2>
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="flex-1">
            <label className="block font-semibold mb-1">Class</label>
            <select
              className="w-full border px-3 py-2 rounded"
              value={selectedClass}
              onChange={e => setSelectedClass(e.target.value)}
              required
            >
              <option value="">Select Class</option>
              {classOptions.map(cls => (
                <option key={cls} value={cls}>{`Class ${cls}`}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block font-semibold mb-1">Batch</label>
            <select
              className="w-full border px-3 py-2 rounded"
              value={selectedBatchId}
              onChange={e => setSelectedBatchId(e.target.value)}
              required
              disabled={!selectedClass || batches.length === 0}
            >
              <option value="">Select Batch</option>
              {batches.map(batch => (
                <option key={batch.id} value={batch.id}>{batch.name}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block font-semibold mb-1">Test Name</label>
            <select
              className="w-full border px-3 py-2 rounded"
              value={selectedTestId}
              onChange={e => setSelectedTestId(e.target.value)}
              required
              disabled={!selectedBatchId || tests.length === 0}
            >
              <option value="">Select Test</option>
              {tests.map(test => (
                <option key={test.id} value={test.id}>{test.testName}</option>
              ))}
            </select>
          </div>
        </div>
        {error && <div className="text-red-600 mb-2">{error}</div>}
        {success && <div className="text-green-600 mb-2">{success}</div>}
      </div>
      {students.length > 0 && selectedTestId && (
        <div className="bg-white rounded-lg shadow p-6">
          <table className="min-w-full border text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2">Sl</th>
                <th className="border px-4 py-2">Name</th>
                <th className="border px-4 py-2">Class</th>
                <th className="border px-4 py-2">Batch</th>
                <th className="border px-4 py-2">Test Name</th>
                <th className="border px-4 py-2">Obtained Marks</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student, idx) => (
                <tr key={student.id}>
                  <td className="border px-4 py-2 text-center">{idx + 1}</td>
                  <td className="border px-4 py-2">{student.name}</td>
                  <td className="border px-4 py-2">{student.class}</td>
                  <td className="border px-4 py-2">{batches.find(b => b.id === student.batchId)?.name || student.batchId}</td>
                  <td className="border px-4 py-2">{tests.find(t => t.id === selectedTestId)?.testName || selectedTestId}</td>
                  <td className="border px-4 py-2">
                    <input
                      type="number"
                      className="w-24 border px-2 py-1 rounded"
                      value={marks[student.id] || ""}
                      onChange={e => handleMarkChange(student.id, e.target.value)}
                      min={0}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            className="mt-4 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Marks"}
          </button>
        </div>
      )}
    </div>
  );
};

export default Marks;
